import { colors, screen } from 'components/styles/variables';
import manufacturerLogosSprite from 'public/static/images/oem-logos-sprite.png';
import convertible from 'public/static/images/text_search/convertible.png';
import coupe from 'public/static/images/text_search/coupe.png';
import crossover from 'public/static/images/text_search/crossover.png';
import hatchback from 'public/static/images/text_search/hatchback.png';
import hybrid from 'public/static/images/text_search/hybrid.png';
import minivan from 'public/static/images/text_search/minivan.png';
import noResults from 'public/static/images/text_search/no_results.png';
import sedan from 'public/static/images/text_search/sedan.png';
import small from 'public/static/images/text_search/small.png';
import sport from 'public/static/images/text_search/sport.png';
import suv from 'public/static/images/text_search/suv.png';
import truck from 'public/static/images/text_search/truck.png';
import wagon from 'public/static/images/text_search/wagon.png';
import { useTranslation } from 'react-i18next';
import logoSpritePositions from 'components/styles/logoSpritePositions';
import { ListItem } from 'types/TextSearch';

interface DropdownInterface {
  list: ListItem[];
  query: string;
  hide: boolean;
  cursor: number | null;
  triggerSearch: (item: ListItem) => void;
}

interface DropdownItemInterface {
  item: ListItem;
  active: boolean;
  triggerSearch: (item: ListItem) => void;
}

const DropdownItem = ({ item, active, triggerSearch }: DropdownItemInterface): JSX.Element => (
  <li className={active ? 'active' : ''} onClick={(): void => triggerSearch(item)}>
    <div className="logo-container">
      <div className={`logo logo--${item.iconClass}`} />
    </div>
    <div>{item.displayName}</div>
    <style jsx>{`
      li {
        display: flex;
        flex-direction: row;
        align-items: center;
        overflow: hidden;
        border-bottom: none;
      }

      li:focus {
        background-color: ${colors.G1};
      }

      .link {
        display: flex;
        align-items: center;
      }

      @media (min-width: ${screen.smMin}) {
        li {
          padding: 5px 10px;
        }

        li:hover,
        .active {
          background-color: ${colors.G1};
        }
      }

      .logo-container {
        width: 48px;
        height: 48px;
      }

      .logo {
        background-image: url('${manufacturerLogosSprite}');
        background-repeat: no-repeat;
        background-size: 80%;
        height: 60px;
        width: 60px;
      }

      .logo--convertible {
        background-image: url(${convertible});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--coupe {
        background-image: url(${coupe});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--crossover {
        background-image: url(${crossover});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--hatchback {
        background-image: url(${hatchback});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--hybrid {
        background-image: url(${hybrid});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--minivan {
        background-image: url(${minivan});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--sedan {
        background-image: url(${sedan});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--small {
        background-image: url(${small});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--sport {
        background-image: url(${sport});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--suv {
        background-image: url(${suv});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--truck {
        background-image: url(${truck});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
      .logo--wagon {
        background-image: url(${wagon});
        background-size: contain;
        background-repeat: no-repeat;
        background-position: -2px 10px;
        width: 48px;
      }
    `}</style>
    <style jsx>{logoSpritePositions}</style>
  </li>
);

const Dropdown = ({ list, query, hide, cursor, triggerSearch }: DropdownInterface): JSX.Element => {
  const { t } = useTranslation();

  if (hide) return <></>;
  const filteredList = list;

  return (
    <div>
      <div data-testid="text-search-dropdown" className="dropdown">
        <hr className="line" />
        <ul>
          {filteredList.length === 0 ? (
            <li className="no-results">
              <div className="no-results-logo-container">
                <div className="no-results-logo" />
              </div>
              <div>{t('commonlyUsed.noResults')}</div>
            </li>
          ) : (
            filteredList.map((listItem, i) => (
              <DropdownItem
                key={listItem.displayName}
                item={listItem}
                active={cursor === i}
                triggerSearch={triggerSearch}
              />
            ))
          )}
        </ul>
      </div>
      <style jsx>{`
        .line {
          display: none;
        }

        @media (min-width: ${screen.smMin}) {
          .line {
            margin: 0;
            padding: 0;
            margin-left: 16px;
            margin-right: 16px;
            box-sizing: border-box;
            display: block;
            border-top: 1px solid ${colors.G2};
            top: 0;
            position: sticky;
          }
        }

        ul {
          list-style: none;
          color: black;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 0;
          margin: 0;
        }

        @media (min-width: ${screen.smMin}) {
          ul {
            padding-top: 8px;
          }
        }

        .dropdown {
          max-height: calc(100vh - 125px);
          width: 100%;
          left: 0px;
          z-index: 12;
          border-radius: 0 0 8px 8px;
          background-color: #ffffff;
          top: 83px;
          position: absolute;
          background: white;
          overflow: scroll;
          display: ${query === '' ? 'none' : 'block'};
          box-sizing: border-box;
        }

        @media (min-width: ${screen.smMin}) {
          .dropdown {
            max-height: 378px;
            max-width: 682px;
            width: calc(100% + 2px);
            top: 54px;
            padding-bottom: 8px;
            overflow-x: hidden;
            box-shadow: 0px 24px 38px rgba(0, 0, 0, 0.2);
          }
        }

        .no-results {
          padding: 5px 16px 5px 16px;
          width: 100%;
          text-align: left;
          font-weight: 400;
          font-size: 16px;
          color: black;
          display: flex;
          align-items: center;
        }

        .no-results-logo-container {
          display: flex;
          align-items: center;
          width: 50px;
          height: 50px;
          justify-content: center;
          margin-right: 8px;
        }

        @media (min-width: ${screen.smMin}) {
          .no-results-logo-container {
            justify-content: left;
            margin-right: 0;
          }

        .no-results-logo {
          background: none;
          background-image: url(${noResults});
          background-size: contain;
          background-repeat: no-repeat;
          height: 32px;
          width: 32px;
        }
      `}</style>
    </div>
  );
};

export default Dropdown;
